package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.notifications.LocalToastState
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.renderComposable
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import kotlin.math.max
import kotlin.math.min

// Mono charges $14.99/month (MONO_CURRENCY=USD); the price is fixed server-side,
// the label here is display-only (mirrors the store priceLabel on mobile).
private const val PRICE_LABEL = "$14.99"

// Trial verification charge — mirrors the backend subscription.trialPriceCents.
private const val TRIAL_PRICE_LABEL = "$0.99"

// Hero cards — complete Figma renders (frame + player baked into one image,
// transparent outside the card silhouette; nodes 11701:242 / 11694:240 /
// 11842:258, exported at 512px width).
private const val HERO_CARD_VOLLEYBALL = "/images/paywall/img_hero_card_volleyball.webp"
private const val HERO_CARD_FOOTBALL = "/images/paywall/img_hero_card_football.webp"
private const val HERO_CARD_TENNIS = "/images/paywall/img_hero_card_tennis.webp"

// News-variant hero — custom match-center art (perspective pitch with
// formation dots, LIVE score bug, timeline + stats panels), rendered from
// scratchpad/hero_news.html at 2048×1152 (16:9). The art already fades into
// the var(--l-s3, #02040B) paywall surface at its bottom and corners.
private const val HERO_NEWS = "/images/paywall/img_hero_news.webp"
private const val HERO_NEWS_ASPECT = 1024.0 / 576.0

/**
 * Which selling pitch the paywall shows. [News] is opened from the news feed
 * banner and gated match-detail clicks: same chrome and CTA, but a
 * match-center hero image and a feature list that leads with the news & match
 * perks. [Default] everywhere else — its list also carries one Match Center
 * row now.
 */
enum class PaywallVariant { Default, News }

// Per-card aspect ratios of the exported renders (width / height).
private const val VOLLEYBALL_CARD_ASPECT = 512.0 / 779.0
private const val FOOTBALL_CARD_ASPECT = 512.0 / 782.0
private const val TENNIS_CARD_ASPECT = 512.0 / 759.0

// Feature panel palette (mobile AppColors.PaywallPanelBorder / PaywallPanelRim
// and AppGradients.PaywallPanel).
private const val PANEL_BORDER = "#3257AD"
private const val PANEL_RIM = "#6594FE"
private const val PANEL_GRADIENT = "linear-gradient(180deg, #0D1119 0%, #000000 100%)"

/** Mobile AppColors.StatYellowCard — the yellow-card chip in the hero stat rows. */
private const val STAT_YELLOW = "#FFFF75"

/** Desktop popup surface — near-black so the hero glow / panel read like the
 *  mobile selling screen (the standard var(--l-s2, #09142d) dialog would wash them out). */
private const val PAYWALL_SURFACE = "#02040B" // theme-invariant: paywall keeps the premium dark hero in both themes

/** Desktop popup content width — also the hero-scaling basis on wide screens. */
private const val POPUP_MAX_WIDTH = 460

/** Above every sheet (1100) and stacked overlay the app uses — the paywall
 *  always wins the stacking order, whatever opened it. */
private const val PAYWALL_MIN_Z = 1400

/**
 * Access-subscription paywall with the "Arena Pro +" selling layout (mobile
 * PaywallScreen port): sport-tinted hero glow + a fan of shop card frames,
 * title with tagline, the glowing feature panel, then the Upgrade CTA (or the
 * active-subscription status with a Cancel action) and the cancel-anytime
 * reassurance underneath.
 *
 * Presented as a centered popup dialog on desktop and as a full-page overlay
 * on phones (user call 19.09.2026); on phones browser back / edge swipe-back
 * closes the paywall instead of leaving the page.
 *
 * Rendered into `document.body` via a portal (the CardShopSection
 * PurchaseConfirmDialog pattern): gates open the paywall from INSIDE
 * BottomSheet content, and the sheet panel's `translateY` transform turns it
 * into the containing block for any `position: fixed` descendant — an inline
 * paywall would be trapped inside (and clipped by) the sheet. The portal
 * escapes that; the caller's CompositionLocals are re-provided inside.
 *
 * Subscribe still redirects to the mono hosted payment page; the pending order
 * is confirmed on return by `resumePendingSubscription`.
 */
@Composable
fun SubscribeModal(
    isOpen: Boolean,
    onClose: () -> Unit,
    source: String = "unknown",
    /** Stacking order hint — clamped up to [PAYWALL_MIN_Z] so the paywall never
     *  loses to the sheet chrome (1100) that opened it. */
    zIndex: Int = 1010,
    variant: PaywallVariant = PaywallVariant.Default,
) {
    LaunchedEffect(isOpen) {
        if (isOpen) AnalyticsTracker.trackClick("paywall_shown", params = mapOf("source" to source))
    }

    if (!isOpen) return

    // Captured for the portal composition — a fresh `renderComposable` root
    // doesn't inherit CompositionLocals from this tree.
    val container = LocalAppContainer.current
    val toast = LocalToastState.current
    val strings = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val effectiveZ = maxOf(zIndex, PAYWALL_MIN_Z)

    DisposableEffect(Unit) {
        val portalRoot = document.createElement("div")
        document.body?.appendChild(portalRoot)
        val composition = renderComposable(portalRoot) {
            CompositionLocalProvider(
                LocalAppContainer provides container,
                LocalToastState provides toast,
                LocalAppStrings provides strings,
                LocalSportColors provides sportColors,
            ) {
                SubscribeModalContent(zIndex = effectiveZ, onClose = onClose, source = source, variant = variant)
            }
        }
        onDispose {
            composition.dispose()
            portalRoot.remove()
        }
    }
}

@Composable
private fun SubscribeModalContent(
    zIndex: Int,
    onClose: () -> Unit,
    source: String = "unknown",
    variant: PaywallVariant = PaywallVariant.Default,
) {
    val container = LocalAppContainer.current
    val toast = LocalToastState.current
    val scope = rememberCoroutineScope()
    val subRepo = container.subscriptionRepository
    val t = LocalAppStrings.current

    val subscription by container.subscription.collectAsState()
    // Trial eligibility comes from the subscription fetch; until it has been
    // attempted the CTA shows a loading state, otherwise a trial-eligible user
    // clicking early would land on the full-price checkout. Guests (no repo)
    // have nothing to load.
    var subscriptionChecked by remember { mutableStateOf(subRepo == null || subscription != null) }
    LaunchedEffect(Unit) {
        container.ensureSubscriptionLoaded()
        subscriptionChecked = true
    }

    // Fresh portal composition per open — plain remember resets naturally.
    var busy by remember { mutableStateOf(false) }

    // Never-subscribed users get the 3-day trial checkout instead of the
    // full-price one; the backend enforces eligibility either way.
    val trialAvailable = subscription?.trialAvailable == true

    val onSubscribe: () -> Unit = onSubscribe@{
        if (busy || !subscriptionChecked) return@onSubscribe
        val repo = subRepo ?: run {
            toast.error(t.subscriptionSignInRequired)
            return@onSubscribe
        }
        busy = true
        scope.launch {
            // The initial fetch may have failed — retry once so the trial
            // decision is never made on a missing subscription.
            if (container.subscription.value == null) container.refreshSubscription()
            val sub = container.subscription.value
            if (sub == null) {
                busy = false
                toast.error(t.subscriptionCheckoutFailed)
                return@launch
            }
            val trial = sub.trialAvailable
            AnalyticsTracker.trackClick(
                "subscription_checkout",
                params = mapOf("method" to "web", "trial" to trial.toString()),
            )
            AnalyticsTracker.trackClick("paywall_purchase_attempt", params = mapOf("source" to source))
            startSubscriptionCheckout(
                scope = scope,
                repository = repo,
                onFailed = { message ->
                    busy = false
                    toast.error(message ?: t.subscriptionCheckoutFailed)
                },
                source = source,
                trial = trial,
            )
        }
    }

    val onCancel: () -> Unit = onCancel@{
        if (busy) return@onCancel
        val repo = subRepo ?: return@onCancel
        busy = true
        AnalyticsTracker.trackClick("subscription_cancel", params = emptyMap())
        scope.launch {
            repo.cancel()
                .onSuccess { sub ->
                    busy = false
                    container.updateSubscription(sub)
                    toast.success(t.subscriptionCanceled)
                }
                .onFailure { e ->
                    busy = false
                    toast.error(e.message ?: t.subscriptionCheckoutFailed)
                }
        }
    }

    if (isMobile()) {
        // Back button / edge swipe-back must close the paywall, not leave the
        // page: opening pushes a same-URL history entry, so the next "back"
        // pops that entry (Kobweb's popstate handler skips route re-init when
        // the path is unchanged, so the page underneath is untouched). Closing
        // any other way consumes the entry to keep history balanced.
        DisposableEffect(Unit) {
            var popped = false
            window.history.pushState(null, "")
            val onPop: (Event) -> Unit = {
                popped = true
                onClose()
            }
            window.addEventListener("popstate", onPop)
            onDispose {
                window.removeEventListener("popstate", onPop)
                if (!popped) window.history.back()
            }
        }

        // Full-page paywall on phones — an opaque fixed overlay instead of the
        // centered dialog; the page (and however the user got here) stays
        // mounted beneath it. The portal root hangs off `body`, so the mobile
        // scale is re-applied here (the BottomSheet/dialog-wrapper pattern) —
        // the desktop `--scale` would render everything at its 0.4 floor.
        Div(
            attrs = {
                style {
                    property("--scale", "var(--mobile-scale)")
                    property("position", "fixed")
                    property("inset", "0")
                    property("z-index", "$zIndex")
                    property("background", PAYWALL_SURFACE)
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("align-items", "center")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("overscroll-behavior", "contain")
                    property("scrollbar-width", "none")
                }
            },
        ) {
            // Flex auto margins center the body when it is shorter than the
            // viewport and collapse to top-aligned scrolling when taller (a
            // grid Box with place-items center would clip the top instead).
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMaxWidth(POPUP_MAX_WIDTH)
                    .styleModifier {
                        property("margin", "auto 0")
                        property("flex-shrink", "0")
                    },
            ) {
                PaywallBody(
                    variant = variant,
                    isSubscribed = subscription?.active == true,
                    cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd == true,
                    currentPeriodEnd = subscription?.currentPeriodEnd,
                    trialAvailable = trialAvailable,
                    loading = !subscriptionChecked,
                    busy = busy,
                    onSubscribe = onSubscribe,
                    onCancel = onCancel,
                )
            }
            // Close — pinned to the viewport corner so it survives scrolling.
            PaywallCloseButton(
                busy = busy,
                onClose = onClose,
                modifier = Modifier
                    .position(Position.Fixed)
                    .sTop(12).sRight(12)
                    .zIndex(2),
            )
        }
        return
    }

    // Centered popup over a dimmed backdrop (desktop / tablets).
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(zIndex)
            .styleModifier { property("background", "rgba(0, 0, 0, 0.65)") }
            .onClick { if (!busy) onClose() },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .sMaxWidth(POPUP_MAX_WIDTH)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("position", "relative")
                    property("background", PAYWALL_SURFACE)
                    property("max-height", "92vh")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("overscroll-behavior", "contain")
                    property("scrollbar-width", "none")
                },
        ) {
            PaywallBody(
                variant = variant,
                isSubscribed = subscription?.active == true,
                cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd == true,
                currentPeriodEnd = subscription?.currentPeriodEnd,
                trialAvailable = trialAvailable,
                loading = !subscriptionChecked,
                busy = busy,
                onSubscribe = onSubscribe,
                onCancel = onCancel,
            )
            // Close — 40px circle pinned to the popup's top-right corner.
            PaywallCloseButton(
                busy = busy,
                onClose = onClose,
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(12).sRight(12),
            )
        }
    }
}

/** 40px close circle — placement (absolute corner vs fixed viewport) comes
 *  from [modifier]. */
@Composable
private fun PaywallCloseButton(busy: Boolean, onClose: () -> Unit, modifier: Modifier) {
    Box(
        modifier = modifier
            .sSize(40)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { if (!busy) onClose() }
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("border", "0.3px solid rgba(255, 255, 255, 0.3)")
            },
        contentAlignment = Alignment.Center,
    ) {
        Icon(src = AppIcons.Close, modifier = Modifier.sSize(18))
    }
}

/**
 * The selling layout itself — shared verbatim between the desktop popup and
 * the mobile bottom sheet; only the chrome around it differs.
 */
@Composable
private fun PaywallBody(
    variant: PaywallVariant,
    isSubscribed: Boolean,
    cancelAtPeriodEnd: Boolean,
    currentPeriodEnd: String?,
    trialAvailable: Boolean,
    /** Subscription (trial eligibility) not fetched yet — CTA is disabled. */
    loading: Boolean,
    busy: Boolean,
    onSubscribe: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current

    // Hero scaling basis — the MEASURED popup width in real px. The popup's
    // CSS width rides the global `--scale` variable, so a raw
    // `min(window.innerWidth, 460)` overshoots on scaled-down desktops and
    // the fan pokes past (and gets clipped by) the dialog. Measuring the
    // rendered container sidesteps the scale entirely.
    var measuredW by remember { mutableStateOf(0.0) }
    val host = remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            host.value?.let { measuredW = it.clientWidth.toDouble() }
        }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }
    val contentW = if (measuredW > 0) measuredW else min(window.innerWidth, POPUP_MAX_WIDTH).toDouble()
    val heroH = max(130.0, min(190.0, contentW * 0.44))

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .attrsModifier {
                ref { el ->
                    host.value = el.unsafeCast<org.w3c.dom.HTMLElement>()
                    measuredW = el.clientWidth.toDouble()
                    onDispose { host.value = null }
                }
            }
            .styleModifier {
                property("position", "relative")
                property("box-sizing", "border-box")
                property("padding-top", s(36))
                property("padding-bottom", s(24))
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Sport-tinted radial hero glow behind the fan.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .top(0.px).left(0.px).right(0.px)
                .sHeight(heroH + 160)
                .styleModifier {
                    property(
                        "background",
                        "radial-gradient(120% 100% at 50% 0%, " +
                            "color-mix(in srgb, ${sportColors.primary.toRgbString()} 30%, transparent) 0%, " +
                            "color-mix(in srgb, ${sportColors.primary.toRgbString()} 8%, transparent) 55%, " +
                            "transparent 80%)",
                    )
                    property("pointer-events", "none")
                },
        ) {}

        if (variant == PaywallVariant.News) NewsHero(heightPx = heroH) else HeroCardFan(heightPx = heroH)

        PaywallTitle(title = t.paywallTitle, accent = sportColors.primary.toRgbString())
        SpanText(
            if (variant == PaywallVariant.News) t.paywallNewsSubtitle else t.paywallSubtitle,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(20)
                .color(org.jetbrains.compose.web.css.Color("#959697"))
                .sMarginTop(8)
                .styleModifier {
                    property("padding", "0 ${s(32)}")
                    property("text-align", "center")
                },
        )

        GoProPanel(variant)

        if (isSubscribed) {
            ActivePanel(
                cancelAtPeriodEnd = cancelAtPeriodEnd,
                currentPeriodEnd = currentPeriodEnd,
                busy = busy,
                onCancel = onCancel,
            )
        } else {
            CtaPill(
                label =
                    when {
                        busy -> t.subscriptionProcessing
                        loading -> t.commonLoading
                        trialAvailable -> t.paywallTrialCta(TRIAL_PRICE_LABEL)
                        else -> t.paywallUpgradeCta(PRICE_LABEL)
                    },
                busy = busy || loading,
                onClick = onSubscribe,
            )
            Row(
                modifier = Modifier.sMarginTop(12).sGap(6),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                TintedCheck(widthPx = 14.0, color = sportColors.primary.toRgbString())
                SpanText(
                    t.paywallCancelAnytime,
                    modifier = Modifier
                        .sFontSize(13)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(16)
                        .color(org.jetbrains.compose.web.css.Color("#BED6F5")),
                )
            }
            SpanText(
                if (trialAvailable) t.paywallTrialTerms(TRIAL_PRICE_LABEL, PRICE_LABEL) else t.paywallAutoRenew,
                modifier = Modifier
                    .sFontSize(11)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(14)
                    .color(org.jetbrains.compose.web.css.Color("#959697"))
                    .sMarginTop(4)
                    .styleModifier {
                        property("padding", "0 ${s(40)}")
                        property("text-align", "center")
                    },
            )
            Row(
                modifier = Modifier.sMarginTop(6).sGap(18),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                PaywallFooterLink(t.homeFooterTerms, href = "/terms")
                PaywallFooterLink(t.homeFooterPrivacy, href = "/privacy")
            }
        }
    }
}

/** "Arena Pro +" — the "Pro +" part tinted with the sport accent. */
@Composable
private fun PaywallTitle(title: String, accent: String) {
    val highlight = "Pro +"
    val idx = title.indexOf(highlight)
    Span(
        attrs = {
            style {
                property("padding", "0 ${s(28)}")
                property("text-align", "center")
                property("font-size", s(28))
                property("font-weight", "600")
                property("line-height", s(34))
                property("color", AppColors.Text.Secondary.toRgbString())
            }
        },
    ) {
        if (idx >= 0) {
            Text(title.substring(0, idx))
            Span(attrs = { style { property("color", accent) } }) { Text(highlight) }
            Text(title.substring(idx + highlight.length))
        } else {
            Text(title)
        }
    }
}

/**
 * Hero — a fan of three cards: left the volleyball render dressed as a
 * pro-event card with the mini stat blocks, right the tennis render, center
 * the red football render. Everything scales uniformly against the 208px base
 * height (mobile parity).
 */
@Composable
private fun HeroCardFan(heightPx: Double) {
    val k = heightPx / 208.0

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", "${heightPx}px")
                property("position", "relative")
            },
    ) {
        HeroCard(
            src = HERO_CARD_VOLLEYBALL,
            widthPx = 114 * k,
            aspect = VOLLEYBALL_CARD_ASPECT,
            offsetX = -72 * k,
            offsetY = 8 * k,
            rotation = -14,
        ) {
            // Pro-event mini stat rows, top-right (mobile HeroStatRow column).
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .styleModifier {
                        property("top", "${46 * k}px")
                        property("right", "${11 * k}px")
                        property("gap", "${4 * k}px")
                    },
            ) {
                HeroStatRow(iconSrc = AppIcons.StatBlockBall, value = "1", k = k)
                HeroStatRow(iconSrc = AppIcons.StatBlockTarget, value = "0", k = k)
                HeroStatRow(iconSrc = null, value = "0", k = k)
            }
        }
        HeroCard(
            src = HERO_CARD_TENNIS,
            widthPx = 114 * k,
            aspect = TENNIS_CARD_ASPECT,
            offsetX = 72 * k,
            offsetY = 8 * k,
            rotation = 14,
        )
        HeroCard(
            src = HERO_CARD_FOOTBALL,
            widthPx = 136 * k,
            aspect = FOOTBALL_CARD_ASPECT,
            offsetX = 0.0,
            offsetY = 0.0,
            rotation = 0,
        )
    }
}

/** News-variant hero — the PRO live-match screenshot in a rounded frame,
 *  sized off the same [heightPx] basis as the card fan so the glow behind it
 *  reads identically. */
@Composable
private fun NewsHero(heightPx: Double) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", "${heightPx}px")
                property("position", "relative")
            },
        contentAlignment = Alignment.Center,
    ) {
        // Frameless: the art's edges are alpha-faded in the asset itself, so
        // it composites straight onto the surface + sport glow — any border,
        // radius or shadow would outline the invisible rectangle.
        Img(
            src = HERO_NEWS,
            attrs = {
                style {
                    property("height", "100%")
                    property("width", "${heightPx * HERO_NEWS_ASPECT}px")
                    property("max-width", "100%")
                    property("object-fit", "contain")
                }
            },
        )
    }
}

/** One hero card — a single baked Figma render, optionally overlaid. */
@Composable
private fun HeroCard(
    src: String,
    widthPx: Double,
    aspect: Double,
    offsetX: Double,
    offsetY: Double,
    rotation: Int,
    overlay: (@Composable () -> Unit)? = null,
) {
    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .styleModifier {
                property("top", "${offsetY}px")
                property("left", "50%")
                property("width", "${widthPx}px")
                property("height", "${widthPx / aspect}px")
                property(
                    "transform",
                    "translateX(calc(-50% + ${offsetX}px))" +
                        if (rotation != 0) " rotate(${rotation}deg)" else "",
                )
            },
    ) {
        Img(
            src = src,
            attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                }
            },
        )
        overlay?.invoke()
    }
}

/** Pro-event mini stat line — icon (null → yellow-card chip) + value. */
@Composable
private fun HeroStatRow(iconSrc: String?, value: String, k: Double) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.styleModifier { property("gap", "${3 * k}px") },
    ) {
        if (iconSrc != null) {
            Icon(
                src = iconSrc,
                modifier = Modifier.styleModifier {
                    property("width", "${11 * k}px")
                    property("height", "${11 * k}px")
                },
            )
        } else {
            Box(
                modifier = Modifier.styleModifier {
                    property("margin", "0 ${1 * k}px")
                    property("width", "${8 * k}px")
                    property("height", "${11 * k}px")
                    property("border-radius", "2px")
                    property("background", STAT_YELLOW)
                },
            ) {}
        }
        SpanText(
            value,
            modifier = Modifier
                .fontWeight(AppTypography.Regular)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5"))
                .styleModifier {
                    property("font-size", "${10 * k}px")
                    property("line-height", "${11 * k}px")
                },
        )
    }
}

/**
 * Feature panel — near-black vertical-gradient card with a `#3257AD` hairline
 * and a uniform blue inner rim glow, holding the 5-row feature checklist
 * (mobile GoProPanel / Figma 11531:212644).
 */
@Composable
private fun GoProPanel(variant: PaywallVariant) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sMarginTop(20)
            .sBorderRadius(32)
            .sGap(18)
            .styleModifier {
                property("box-sizing", "border-box")
                property("width", "calc(100% - ${s(32)})")
                property("padding", "${s(20)} ${s(16)}")
                property("background", PANEL_GRADIENT)
                property("border", "0.5px solid $PANEL_BORDER")
                // Inner rim glow — CSS inset shadows stand in for the mobile
                // concentric fading strokes (peak alpha 0.28, cubic falloff).
                property(
                    "box-shadow",
                    "inset 0 0 12px color-mix(in srgb, $PANEL_RIM 28%, transparent), " +
                        "inset 0 0 30px color-mix(in srgb, $PANEL_RIM 12%, transparent)",
                )
            },
    ) {
        // The panel is capped at 5 rows (user rule) — overflow gets merged.
        if (variant == PaywallVariant.News) {
            PanelFeatureRow(t.paywallFeatureUnlimitedNews)
            PanelFeatureRow(t.paywallFeatureHighlights)
            PanelFeatureRow(t.paywallFeatureLiveScores)
            PanelFeatureRow(t.paywallFeatureLineups)
            PanelFeatureRow(t.paywallFeaturePlayerProfiles)
        } else {
            PanelFeatureRow(t.paywallFeaturePlayerCards)
            PanelFeatureRow(t.paywallFeatureAiAvatar)
            PanelFeatureRow(t.paywallFeatureActivityLifestyle)
            PanelFeatureRow(t.paywallFeatureProEvents)
            PanelFeatureRow(t.paywallFeatureNewsMatches)
        }
    }
}

@Composable
private fun PanelFeatureRow(label: String) {
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier.fillMaxWidth().sGap(12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TintedCheck(widthPx = 18.0, color = sportColors.primary.toRgbString())
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(20)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5")),
        )
    }
}

/** ic_check re-colored via a CSS mask — sport-tinted checkmark (18.51:13 art). */
@Composable
private fun TintedCheck(widthPx: Double, color: String) {
    val heightPx = widthPx * 13.0 / 18.51
    Box(
        modifier = Modifier.styleModifier {
            property("width", "${widthPx}px")
            property("height", "${heightPx}px")
            property("flex-shrink", "0")
            property("background-color", color)
            property("mask", "url(${AppIcons.Check}) no-repeat center / contain")
            property("-webkit-mask", "url(${AppIcons.Check}) no-repeat center / contain")
        },
    ) {}
}

/** 52px sport-gradient Upgrade pill with a tinted drop shadow. */
@Composable
private fun CtaPill(label: String, busy: Boolean, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    val primary = sportColors.primary.toRgbString()
    val secondary = sportColors.secondary.toRgbString()
    Box(
        modifier = Modifier
            .sHeight(52)
            .sMarginTop(16)
            .sBorderRadius(100)
            .styleModifier {
                property("box-sizing", "border-box")
                property("width", "calc(100% - ${s(32)})")
                property("background", "linear-gradient(180deg, $primary 0%, $secondary 100%)")
                property("border", "0.3px solid rgba(255, 255, 255, 0.3)")
                property(
                    "box-shadow",
                    "0 10px 28px color-mix(in srgb, $primary 45%, transparent)",
                )
                if (busy) property("opacity", "0.85")
            }
            .then(if (busy) Modifier else Modifier.cursor(Cursor.Pointer).onClick { onClick() }),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(sportColors.onPrimary),
        )
    }
}

/** Already-subscribed footer — status + renewal date + Cancel action. */
@Composable
private fun ActivePanel(
    cancelAtPeriodEnd: Boolean,
    currentPeriodEnd: String?,
    busy: Boolean,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier.sMarginTop(20).sGap(8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TintedCheck(widthPx = 16.0, color = sportColors.primary.toRgbString())
        SpanText(
            t.subscriptionActive,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(20)
                .color(org.jetbrains.compose.web.css.Color("#BED6F5")),
        )
    }
    val end = formatDate(currentPeriodEnd)
    SpanText(
        if (cancelAtPeriodEnd) {
            if (end != null) t.subscriptionEndsOn(end) else t.subscriptionWillCancel
        } else {
            if (end != null) t.subscriptionRenewsOn(end) else t.subscriptionActive
        },
        modifier = Modifier
            .sFontSize(13)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(18)
            .color(org.jetbrains.compose.web.css.Color("#959697"))
            .sMarginTop(6)
            .styleModifier { property("text-align", "center") },
    )
    if (!cancelAtPeriodEnd) {
        Box(
            modifier = Modifier
                .sHeight(46)
                .sMarginTop(14)
                .sBorderRadius(100)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                    if (busy) property("opacity", "0.5")
                }
                .then(if (busy) Modifier else Modifier.cursor(Cursor.Pointer).onClick { onCancel() }),
            contentAlignment = Alignment.Center,
        ) {
            GradientBorderRing(radiusPx = 100)
            Box(
                modifier = Modifier.styleModifier { property("padding", "${s(14)} ${s(32)}") },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    if (busy) t.subscriptionProcessing else t.cancelSubscription,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(18)
                        .color(org.jetbrains.compose.web.css.Color("#BED6F5")),
                )
            }
        }
    }
}

/** Small muted footer link (Terms / Privacy) — opens in a new tab so the
 *  paywall (and whatever opened it) stays put. */
@Composable
private fun PaywallFooterLink(label: String, href: String) {
    A(
        href = href,
        attrs = {
            attr("target", "_blank")
            style {
                property("color", AppColors.Text.Muted.toRgbString())
                property("font-size", s(13))
                property("line-height", s(16))
                property("text-decoration", "none")
            }
        },
    ) {
        Text(label)
    }
}

/** ISO date → "Jan 15, 2026". Returns null if unparseable. */
private fun formatDate(iso: String?): String? {
    if (iso.isNullOrBlank()) return null
    val ms = kotlin.js.Date.parse(iso)
    if (ms.isNaN()) return null
    val d = kotlin.js.Date(ms)
    return try {
        d.toLocaleDateString("en-US", dateOptions())
    } catch (_: Throwable) {
        null
    }
}

private fun dateOptions(): dynamic = js("({ year: 'numeric', month: 'short', day: 'numeric' })")
